import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { OrderingMarketplace } from './useGetSettings';

export type NotificationType = (
    'new-order' |
    'quotes-extended' |
    'auto-requote' |
    'quotes-back' |
    'quotes-back-potential-total' |
    'quotes-back-quotes-only' |
    'no-quoted-parts' |
    'flagged-parts' |
    'orders-confirmed' |
    'orders-rejected' |
    'orders-returned-cancelled' |
    'rekeying-completed' |
    'rekeying-completed-flagged' |
    'rekeying-skipped' |
    'tesla-order-error' |
    'part-number-changed' |
    'pre-order-backorder' |
    'no-oem-quotes' |
    'back-order-ordered' |
    'order-mention' |
    'invoice-mention' |
    'vendor-message' |
    'supplier-changes' | 'needs-rekey' | 'rekey-advisor-score' | 'order-not-confirmed' | 'order-request-enabled' |
    'flagged-invoices' |
    'negotiation-handoff' |
    'order-placement-failed' |
    'ccc-manual-po-created' |
    'ccc-manual-po-failed' |
    'rekey-quote-only-completed' |
    'inventory-stock-request' |
    'inventory-stock-request-placed'
);

export type NotificationChannel = 'email' | 'notif';

export type EnabledNotificationTypesDict = Partial<Record<NotificationType, NotificationChannel[]>>;

interface NotificationPreview {
    vehicle?: string;
    roNumber?: string;
}

export interface NotificationItem {
    _id: string;
    type: NotificationType;
    data:
    | { type: 'ems'; emsId: string; }
    // `source` is only set on order-placement-failed notifications written after
    // the marketplace-agnostic consolidation (and backfill) — treat as optional.
    | { type: 'order'; orderId: string; source?: OrderingMarketplace; }
    | { type: 'invoice'; invoiceId: string; }
    | {
        type: 'negotiation';
        negotiationId: string;
        invoiceId: string;
        orderId?: string;
    }
    | { type: 'stock-request'; stockRequestId: string; orderId?: string; }
    | { type: 'purchase-order'; purchaseOrderId: string; orderId?: string; };
    read: boolean;
    timeCreated: Date;
    preview?: NotificationPreview;
    senderName?: string;
    explicit?: boolean;
    /**
     * Set when the notification is for another organization — a Yaxle employee tagged
     * from a shop they aren't working in. Opening it switches there first.
     */
    otherOrganization?: { id: string; name: string; };
}

export interface AnnouncementItem {
    _id: string;
    message: string;
    senderName: string;
    timeCreated: string;
}

export interface GetNotificationsResponse {
    notifications: NotificationItem[];
    unreadCount: number;
    hasMore: boolean;
    notificationsEnabled: boolean;
    enabledNotificationTypes?: EnabledNotificationTypesDict;
    /** All roles: unread order-mention count (messages + @mentions) powering the New Chat badge. */
    unreadMentionCount?: number;
    /** Unread org-wide announcements, delivered via the notifications poll. */
    announcements?: AnnouncementItem[];
}

/**
 * Whether the viewer's personal preferences mute in-app alerts (toast + bell
 * drawer) for this notification type. Mirrors NotificationToastManager's gate.
 * Order-scoped surfaces (order page sidebar/drawer) still display muted types
 * as an audit trail (ENG-2136); use this to label those rows as muted.
 */
export const isNotificationTypeMuted = (
    type: NotificationType,
    notificationsEnabled?: boolean,
    enabledNotificationTypes?: EnabledNotificationTypesDict,
): boolean => {
    if (notificationsEnabled === false) return true;
    if (enabledNotificationTypes && Object.keys(enabledNotificationTypes).length > 0) {
        const channels = enabledNotificationTypes[type];
        if (channels !== undefined && !channels.includes('notif')) return true;
    }
    return false;
};

interface GetNotificationsParams {
    search?: string;
    offset?: number;
    type?: string;
    orderId?: string;
}

const getNotifications = async (
    sessionId: string | null,
    params?: GetNotificationsParams
): Promise<GetNotificationsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.type) queryParams.set('type', params.type);
    if (params?.orderId) queryParams.set('orderId', params.orderId);

    const queryString = queryParams.toString();
    const url = `${API_URL}/notifications${queryString ? `?${queryString}` : ''}`;

    const response = await axios.get(url, {
        headers: {
            Authorization: sessionId
        }
    });
    return response.data;
};

export const useGetNotifications = (
    enabled: boolean = true,
    search?: string,
    offset?: number,
    type?: NotificationType,
    orderId?: string
) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: ['/notifications', search, offset, type, orderId],
        queryFn: () => getNotifications(sessionContext?.sessionId ?? null, { search, offset, type, orderId }),
        enabled: !!sessionContext?.sessionId && enabled,
        refetchInterval: !search ? 10000 : undefined,
    });
};
